import { useCallback, useMemo, useState } from 'react';
import { GROUP_AUTO_COLLAPSE_THRESHOLD } from '@elementary/common-ui/constants';

type Args = {
  groupTitles: string[];
  resetKey: string | null;
};

type ToggleState = {
  resetKey: string | null;
  overrides: Record<string, boolean>;
};

const useGroupExpansion = ({ groupTitles, resetKey }: Args) => {
  const [toggleState, setToggleState] = useState<ToggleState>({
    resetKey,
    overrides: {},
  });

  const overrides = useMemo(
    () => (toggleState.resetKey === resetKey ? toggleState.overrides : {}),
    [toggleState, resetKey],
  );

  const isExpandedByDefault =
    groupTitles.length <= GROUP_AUTO_COLLAPSE_THRESHOLD;

  const expandedGroups = useMemo(
    () =>
      Object.fromEntries(
        groupTitles.map((title) => [
          title,
          overrides[title] ?? isExpandedByDefault,
        ]),
      ),
    [groupTitles, overrides, isExpandedByDefault],
  );

  const expandedTitles = useMemo(
    () => groupTitles.filter((title) => expandedGroups[title]),
    [groupTitles, expandedGroups],
  );

  const onGroupToggle = useCallback(
    (title: string) => {
      setToggleState((prev) => {
        const prevOverrides = prev.resetKey === resetKey ? prev.overrides : {};
        const isExpanded = prevOverrides[title] ?? isExpandedByDefault;
        return {
          resetKey,
          overrides: { ...prevOverrides, [title]: !isExpanded },
        };
      });
    },
    [resetKey, isExpandedByDefault],
  );

  return { expandedGroups, expandedTitles, onGroupToggle };
};

export default useGroupExpansion;
